/**
 * アプリ全体の定数。マジックナンバーをここに集約する。
 */
export const CAMERA = Object.freeze({
  fov: 65,
  near: 0.05,
  far: 200,
  /** 視点移動アニメーションの所要時間(ms) */
  transitionMs: 1100,
});

/**
 * カメラ操作の感度。
 *
 * 【重要】キーボード操作とポインタ操作は単位がまったく違う。
 * キーボードは「1秒あたりの移動量・回転量」、ポインタは「1ピクセルあたりの量」。
 * Spark の既定値は keyboard.rotate=2 に対して pointer.rotate=0.002 と1000倍違う。
 * ここを取り違えると、わずかなドラッグでカメラが物件の外まで飛ぶ。
 */
export const CONTROLS = Object.freeze({
  /** キーボード操作（WASD・矢印キー）。Spark既定: move=1, rotate=2 */
  keyboard: Object.freeze({
    moveSpeed: 1.1,
    rotateSpeed: 1.6,
  }),

  /** ポインタ操作（ドラッグ・ホイール・ピンチ）。Spark既定: rotate=0.002, slide=0.006, scroll=0.0015 */
  pointer: Object.freeze({
    // 内見では見回しが主操作なので、既定よりわずかに落ち着かせる
    rotateSpeed: 0.0018,
    slideSpeed: 0.005,
    scrollSpeed: 0.0012,
    // 慣性。Spark既定はどちらも0.15
    moveInertia: 0.2,
    rotateInertia: 0.15,
  }),
});

/** 操作キャラクター（キンクマハムスター） */
export const AVATAR = Object.freeze({
  /** 部屋の中で見失わない大きさ。実物のハムスターより大きめにしている */
  scale: 0.8,
  /** 飼っているキンクマの写真に合わせた色 */
  colors: Object.freeze({
    fur: 0xe4bb8c,
    belly: 0xf6e6d0,
    earOuter: 0x8c7566,
    earInner: 0xc9a79c,
    eye: 0x130e0c,
    nose: 0xe4a0aa,
    paw: 0xf0c3c6,
  }),
});

/** 歩行モード */
export const WALK = Object.freeze({
  /** 歩く速さ(m/s)。内見なので実際の歩行よりゆっくり */
  speed: 1.5,
  /** カメラがキャラの後ろに取る距離と高さ(m)。近すぎるとキャラで部屋が隠れて見にくい */
  cameraDistance: 2.6,
  cameraHeight: 1.5,
  /** カメラの追従の滑らかさ。1に近いほど機敏 */
  cameraLerp: 0.12,
  /** キャラの向き直りの速さ */
  turnLerp: 0.18,
  /** 壁とみなす距離(m)。これより近い面には進めない */
  wallClearance: 0.45,
  /**
   * 壁を探すレイの間隔(ms)。
   * レイ1本のコストはスプラット数に比例し、実物件（100万規模）では
   * 10ms を超える。毎フレーム撃つと歩けなくなるので必ず間引く。
   */
  wallProbeMs: 110,
  /** 前回の壁判定を使い回してよい向きのズレ(ラジアン)。これを超えたら測り直す */
  wallProbeAngle: 0.5,
  /** 床を探すレイの間隔(ms)。毎フレーム撃つとスプラット数ぶん重い */
  groundProbeMs: 120,
  /** 床の高さに寄せる滑らかさ */
  groundLerp: 0.25,
  /** 床が見つからないときに保つ高さ(m) */
  fallbackY: 0,
  /**
   * 歩行中はカメラからこの距離(m)より手前の点を描かない。
   * 三人称カメラは部屋の中を動くため、空中に残った誤差の点が目の前に来て
   * 大きなぼやけとして映る。それを消すための設定。
   */
  nearClip: 0.4,
  /** カメラが注視するキャラの高さ(m) */
  lookAtHeight: 0.3,
  /** 視点（viewpoints）の目線が、立っている面から何m上にあるか。視点ボタンで立たせる階を決めるのに使う */
  viewpointEyeHeight: 1.45,
  /** 天井の下にカメラを収めるときの余白と、下げてよい最低の高さ(m) */
  ceilingMargin: 0.2,
  minCameraHeight: 0.35,
  /** カメラとキャラの距離が fadeFar(m) を切ったら薄くし始め、fadeNear で fadeMinOpacity まで下げる */
  fadeNear: 0.8,
  fadeFar: 1.4,
  fadeMinOpacity: 0.2,
  /** ズームで変えられるカメラ距離の倍率の範囲 */
  minDistanceScale: 0.3,
  maxDistanceScale: 2.0,
});

/**
 * ズーム（マウスホイール・画面右のスライダー）。
 * 0 = いちばん引き（広く見る）、1 = いちばん寄り。見回しでは視野角、歩行ではカメラ距離を変える。
 */
export const ZOOM = Object.freeze({
  initial: 0.5,
  /** 見回しの視野角(度)。initial のとき CAMERA.fov と同じになるように置く */
  fovWide: 95,
  fovNarrow: 35,
  /** 歩行のカメラ距離の倍率 */
  distanceWide: 2.0,
  distanceNarrow: 0.3,
  /** ホイール1ノッチ(deltaY=100)あたりの変化量 */
  wheelStep: 0.06,
});

/**
 * 歩行用の当たり判定マップ（navGrid.js）。
 * 数値は GK702 の実スキャン（約70万スプラット）で調整した。
 * 密度のしきい値はスキャンの細かさで変わるため、物件によっては調整が要る。
 */
export const NAV = Object.freeze({
  /** マスの大きさ(m) */
  cell: 0.1,
  /** 撮影地点からこの半径(m)の範囲だけを調べる */
  radius: 4,
  /** 判定に使うスプラット。薄いもの・大きくぼやけたもの（空中のゴミ）は除く */
  minOpacity: 0.5,
  maxSplatScale: 0.08,
  minSplats: 5000,
  /** 床の検出。撮影地点よりこれ以上低い面から、最も密な高さを床とする */
  floorBin: 0.05,
  minEyeHeight: 0.3,
  floorPeakRatio: 0.15,
  /** 最も密な高さのこの割合以上ある高さのうち、一番低いものを床とする（天井・ロフトを避ける） */
  floorCandidateRatio: 0.5,
  /** 家具とみなす高さ帯(床からm)と、マスあたりの最低スプラット数 */
  lowMin: 0.12,
  lowMax: 0.7,
  furnitureThreshold: 20,
  /** 壁とみなす高さ帯(床からm)と、マスあたりの最低スプラット数 */
  highMin: 1.0,
  highMax: 2.3,
  wallThreshold: 25,
  /** キャラの体の半径（マス数）。壁や家具からこのぶん離す */
  bodyCells: 1,
  /** 歩ける面積がこれ未満なら判定失敗とみなし、従来のレイ判定に戻す */
  minAreaM2: 1.5,
  /** カメラが壁の向こうに回り込まないよう、キャラとの距離を縮める下限(m) */
  minCameraDistance: 0.45,
  /**
   * メッシュのスキャン用。表面に1㎡あたりこの数だけ点をばらまき、スプラットと同じ判定に載せる。
   * 壁 10cm × 高さ帯1.3m で約390点となり、wallThreshold を十分に超える。
   */
  meshSampleDensity: 3000,
  /** メッシュ用の床判定の最低割合（スプラットの floorPeakRatio に相当） */
  meshFloorPeakRatio: 0.02,
  /** CG の物件（設計データ）の点の密度。面が多いので粗くする */
  planSampleDensity: 1500,
  /** メッシュは撮影開始地点が分からず部屋全体を含めたいので、範囲を広めに取る */
  meshRadius: 6,
  /** 面の法線の上下成分がこれを超えたら水平面（床・天井・棚板）とみなす */
  horizontalNormal: 0.7,
});

/**
 * 段差を上り下りできる歩行マップ（navLayers.js、メッシュ用）。
 * GK702 のメッシュ（中二階 約1.2m・ロフト 約2.0m・船梯子状の階段）で調整した。
 */
export const NAV_LAYERS = Object.freeze({
  cell: 0.1,
  /** 高さの刻み(m) */
  bin: 0.05,
  /** 上向き成分がこれを超える面を「立てる面」「水平な物」とみなす（約60度より緩い傾き） */
  upFacing: 0.5,
  /** 1つの面とみなす最低の点数（1マスあたり） */
  minSurfacePoints: 3,
  /** 1回で上り下りできる段差(m)。階段の蹴上げ＋余裕 */
  step: 0.32,
  /** 面のすぐ上 overheadMin〜headroom に水平な物が maxOverheadPoints 以上あれば立てない（テーブルの下など） */
  overheadMin: 0.12,
  headroom: 0.45,
  maxOverheadPoints: 20,
  /** 面から「一段＋sideMargin」〜sideMax の高さに縦の面が maxSidePoints 以上あれば立てない（壁・家具の側面） */
  sideMargin: 0.05,
  sideMax: 0.8,
  maxSidePoints: 30,
  /**
   * 敷居越え。浴室の入口などの細い盛り上がり（メッシュでは30〜50cmに膨らむ）を通す。
   * 幅 bridgeCells マス以内・高さ bridgeMaxRise 以内で、床から bridgeWallMin〜bridgeWallMax に
   * 縦の面の点が bridgeWallPoints 未満（壁ではない）なら通過できる。
   */
  bridgeCells: 3,
  bridgeMaxRise: 0.6,
  bridgeWallMin: 0.7,
  bridgeWallMax: 1.6,
  bridgeWallPoints: 25,
  /** 天井の判定（カメラ用）。今の高さより ceilingMinClearance 以上上の、点が ceilingPoints 以上ある水平面 */
  ceilingMinClearance: 0.75,
  ceilingPoints: 6,
  /** 開始地点に面が無いとき、周りを探すマス数 */
  startSearchCells: 20,
  minAreaM2: 1.5,
  /** カメラ用の壁判定: 床から wallMin〜wallMax に縦の面の点が wallPoints を超えるマス */
  wallMin: 0.9,
  wallMax: 2.0,
  wallPoints: 40,
});

/**
 * シンプル表示・マップ表示（simpleMap.js / navLayers.js の見取り）。
 * スキャンのぐにゃぐにゃを見せず、床・壁・家具を箱で描き直す。
 */
export const MAP = Object.freeze({
  /** 歩ける床からこのマス数以内だけ描く（部屋の外のゴミを出さない） */
  nearCells: 3,
  /** 地図は navLayers のマスをこの倍率でまとめて描く（10cm × 2 = 20cm） */
  coarse: 2,
  /** 同じマスの床をこの高さの差(m)までは同じ段とみなす */
  levelGap: 0.15,
  /** 一番下の床からこの高さ(m)以内の床は、平らな床として描く */
  flattenRise: 0.22,
  /** 床の穴埋めを繰り返す回数 */
  fillPasses: 2,
  /** 壁はこのマス数以内に床があるものだけ残す */
  wallFloorReach: 2,
  /** 壁の高さ(m)。そろえて描く */
  wallHeight: 2.4,
  /** 床板の厚さ(m) */
  slab: 0.06,
  /** 下の床からこの高さまでの段は、下まで埋めた段にする */
  solidStepMax: 1.0,
  /** 壁の高さ: 縦の面の点の上から数%を除いた高さ。上限 wallMaxHeight */
  wallTopQuantile: 0.97,
  wallMaxHeight: 3.2,
  /** 色を拾うとき、面の高さからこの差(m)以内の点を使う */
  colorBand: 0.08,
  /** スキャンの色は撮影時の暗さが残るので、少し明るくして描く */
  colorBoost: 1.2,
  /** 家具の色の下限（0〜1）。真っ黒だと床の穴に見えるため */
  furnitureMinBrightness: 0.22,
  /** 見出し（部屋の名前）の大きさ。画面の高さに対する割合 */
  labelHeight: 0.035,
  /** 見出しを床からこの高さ(m)に置く */
  labelLift: 0.9,
  /** 家具: 床から furnitureMin〜furnitureMax に、furniturePoints 以上の水平面 */
  furnitureMin: 0.15,
  furnitureMax: 1.5,
  furniturePoints: 6,
  colors: Object.freeze({
    floor: 0xd9b98c,
    floorUpper: 0xe8d3a8,
    wall: 0xf4f1ea,
    furniture: 0x8fa3b8,
    background: 0x1b2330,
  }),
  /** マップ表示: 今いる床からこの高さより上を切り取って、上から部屋が見えるようにする */
  cutHeight: 1.2,
  /** マップ表示のカメラの高さ(m)。ズームの引き〜寄り */
  heightWide: 26,
  heightNarrow: 6,
  /** マップ表示の視野角(度)。狭くして高い所から見ると、壁が外側へ倒れて見える歪みが減る */
  fov: 28,
  /** マップ表示のカメラの傾き（高さに対する手前へのずらし量）。斜めから見ると立体に見える */
  tilt: 0.55,
  /** マップ表示のときの背景色。間取り図のように明るい下地にする */
  background: 0xe9ecef,
});

/**
 * スキャンに CG を肉付けする（planFromLayout.js）ときの設定。
 * 写真からの推測ではなく、スキャンで測った床の高さ・壁・家具をそのまま使う。
 */
export const SCAN_CG = Object.freeze({
  /** この面積(㎡)以上ある高さを「階」とみなす。それより小さい高さは階段の段 */
  levelMinAreaM2: 1.5,
  /** 階の高さの許容差(m)。これ以内の床は同じ階として同じ高さにそろえる */
  levelTolerance: 0.25,
  /** 階を数えるとき、この差(m)以内の高さはひとつの階にまとめる（スキャンの床は波打つため） */
  levelMergeGap: 0.3,
  /** 床板の最低の厚さ(m) */
  minSlab: 0.06,
  /** 階ごとの床の質感（下の階から順）。足りなければ最後のものを使う */
  floorTextures: Object.freeze(['oak', 'walnut', 'oakLight']),
  stepTexture: 'walnut',
  underTexture: 'woodCeiling',
  ceilingTexture: 'plaster',
  ceilingThick: 0.08,
  /** 床からこれ以上高い所にある水平面だけを天井とみなす(m) */
  minCeilingHeight: 1.6,
  /** 天井の高さを決めるのに、最低これだけの数の測定点が要る。足りなければ defaultCeilingAbove を使う */
  ceilingMinSamples: 30,
  /** スキャンに天井が無いとき、一番上の階の床からこの高さ(m)に天井を張る */
  defaultCeilingAbove: 2.3,
  wallTexture: 'plaster',
  /** 床の外周に立てる壁の厚さ(m) */
  wallThickness: 0.12,
  /** 壁を立てる前に床の縁をならすマス数（1マス＝0.2m）。壁が細切れになるのを防ぐ */
  wallSmoothCells: 3,
  /** 段として残す最低の面積(㎡)。これより小さい段は、階段でなければ床にならす */
  stepKeepAreaM2: 0.3,
  /** 階段の一段とみなす高さの差(m)。隣にこれだけ高さの違う段があれば階段 */
  stairRiseMin: 0.14,
  stairRiseMax: 0.36,
  /** 家具を当てるとき、かたまりがこの割合より詰まっていないと家具の形とみなさない */
  guessMinFill: 0.5,
  /** 家具の場所が「どの部屋か」を、この距離(m)以内の視点の名前で決める */
  guessRoomRadius: 2.2,
  /** 部屋の輪郭をまっすぐにそろえる幅（マス）。これ以内のギザギザは1枚の壁にならす */
  wallSnapCells: 2,
  /** 部屋の中の仕切り壁にする最低の長さ(m)と、壁とみなす厚さの上限(m)。短い塊は家具なので拾わない */
  innerWallMinLength: 1.0,
  innerWallMaxThickness: 0.3,
  /** かたまりがこの割合より詰まっていたら、壁ではなく家具とみなす */
  innerWallMaxFill: 0.35,
  /** 視点の足元に敷く床の半径（マス）。スキャンで床が抜けていても視点から見渡せるようにする */
  spotFloorCells: 4,
  /** 視点の足元の高さが階の高さからこれ(m)以上離れていたら、その階の床にはしない */
  spotLevelTolerance: 0.6,
  /** 天井が低い所でも、壁はこの高さ(m)以上にする */
  minWallHeight: 1.0,
  /** 中二階・ロフトの吹き抜け側につける手すりの高さ(m)と厚さ(m) */
  railingHeight: 0.95,
  railingThickness: 0.06,
  railingTexture: 'walnut',
  /** 床を壁際へ広げるマス数（1マス＝0.2m）。壁沿いの黒い筋を消す */
  floorGrowCells: 2,
  /** 隣り合う家具のマスの足元の高さがこの差(m)以内なら同じ家具 */
  clusterBaseTolerance: 0.3,
  /** これより小さい家具のかたまりはゴミとみなす(㎡) */
  furnitureMinAreaM2: 0.04,
  /** 照明。スキャンの物件ではキャラのライトもあるので控えめにする */
  ambient: 1.7,
  sun: 0.6,
});

export const RENDER = Object.freeze({
  /** 端末の解像度倍率上限。スマホでの発熱・電池消費を抑える */
  maxPixelRatio: 2,
  maxPixelRatioMobile: 1.5,
  background: 0x0d1117,
});

export const MEASURE = Object.freeze({
  /**
   * これ未満の不透明度のスプラットはクリック判定から除外する。
   * 高くすると薄い面（カーテン・窓枠・家具の縁）がクリックできなくなるため低めにする。
   */
  minRaycastOpacity: 0.12,
  markerRadius: 0.035,
  markerColor: 0x22d3ee,
  lineColor: 0x22d3ee,
});

/** 物件データ・スプラットファイルの配信元 */
export const ENDPOINTS = Object.freeze({
  property: (id) => `/api/properties/${encodeURIComponent(id)}`,
  splat: (key) => `/splat/${key.split('/').map(encodeURIComponent).join('/')}`,
  model: (key) => `/model/${key.split('/').map(encodeURIComponent).join('/')}`,
  ai: '/api/ai/ask',
});
